import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import {
  Loader2,
  RefreshCw,
  MessageSquare,
  Menu
} from 'lucide-react';
import { useConversationHistory } from '../Hooks/chatBot/useConversationHistory';
import { useStreamingAnalysis } from '../Hooks/chatBot/useStreamingAnalysis';
import { useAdvertiserToken } from '../Hooks/chatBot/useAdvertiserToken';
import ConversationSidebar from './components/ConversationSidebar';
import ChatInput from './components/ChatInput';
import BlockRenderer from './components/BlockRenderer';
import ErrorBlock from './components/blocks/ErrorBlock';

// Main Analytics Component
const DV360Analytics = () => {
  const [question, setQuestion] = useState('');
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [isLoadingConversation, setIsLoadingConversation] = useState(false);
  const bottomRef = useRef(null);

  // Initialize advertiser token
  const {
    token: advertiserToken,
    loading: tokenLoading,
    error: tokenError
  } = useAdvertiserToken();

  // Streaming analysis hook
  const {
    blocks,
    isStreaming,
    error,
    analyzeData,
    cancelAnalysis,
    setBlocksDirectly
  } = useStreamingAnalysis(advertiserToken);

  // Conversation history with localStorage
  const {
    conversations,
    activeConversation,
    createNewConversation,
    updateConversationBlocks,
    selectConversation,
    deleteConversation,
    getCurrentConversation,
    startNewConversation,
  } = useConversationHistory();

  // Update conversation blocks when blocks change (only during streaming)
  useEffect(() => {
    if (activeConversation && blocks.length > 0 && isStreaming) {
      updateConversationBlocks(activeConversation, blocks);
    }
  }, [blocks, activeConversation, isStreaming, updateConversationBlocks]);

  // Handle conversation selection with loading state
  const handleSelectConversation = useCallback(async (convId) => {
    if (convId === activeConversation) return;

    setIsLoadingConversation(true);
    selectConversation(convId);

    // Small delay to prevent infinite loops
    setTimeout(() => {
      const conv = conversations.find(c => c.id === convId);
      if (conv) {
        setQuestion(conv.question);
        setBlocksDirectly(conv.blocks || []);
      }
      setIsLoadingConversation(false);
    }, 100);
  }, [activeConversation, selectConversation, conversations, setBlocksDirectly]);

  // Handle suggestion clicks
  const handleSuggestionClick = (suggestion) => {
    setQuestion(suggestion);
    handleSubmitAnalysis(suggestion);
  };

  // Submit analysis
  const handleSubmitAnalysis = async (questionToAnalyze = question) => {
    if (!questionToAnalyze.trim()) return;

    let convId = activeConversation;
    if (!convId) {
      convId = createNewConversation(questionToAnalyze);
    }

    setBlocksDirectly([]);
    await analyzeData(questionToAnalyze);
  };

  // Handle submit button click
  const handleSubmit = () => {
    handleSubmitAnalysis();
  };

  // Handle new conversation
  const handleNewConversation = () => {
    startNewConversation();
    setQuestion('');
    setBlocksDirectly([]);
  };

  // Handle conversation deletion
  const handleDeleteConversation = (convId) => {
    deleteConversation(convId);
  };

  // Auto-scroll to bottom when new blocks are added
  useEffect(() => {
    if (blocks.length > 0) {
      bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
    }
  }, [blocks]);

  // Example questions
  const exampleQuestions = [
    "Show me top 5 advertisers by revenue",
    "What are the impression trends over time?",
    "Compare CTR across different campaigns",
    "Analyze cost per click by advertiser",
    "Show me sample data from the table"
  ];

  const isNewConversation = !activeConversation;

  // Filter blocks to hide code blocks and show only highest progress
  const visibleBlocks = useMemo(() => {
    const filtered = blocks.filter(block => block.type !== 'code');

    // Check if there's an error block
    const hasError = filtered.some(block => block.type === 'error');

    // For progress blocks, only show the highest percentage one
    const progressBlocks = filtered.filter(block => block.type === 'progress');
    const otherBlocks = filtered.filter(block => block.type !== 'progress');

    // If there's an error, don't show any progress blocks
    if (hasError) {
      return otherBlocks;
    }

    let highestProgressBlock = null;
    if (progressBlocks.length > 0) {
      highestProgressBlock = progressBlocks.reduce((highest, current) =>
        current.data.percentage > highest.data.percentage ? current : highest
      );
    }

    return highestProgressBlock ? [...otherBlocks, highestProgressBlock] : otherBlocks;
  }, [blocks]);

  return (
    <div className="flex flex-1 overflow-hidden" style={{ backgroundColor: '#EFEFEF',paddingTop:"60px" }}>
      {/* Sidebar */}
      <ConversationSidebar
        conversations={conversations}
        activeConversation={activeConversation}
        onSelectConversation={handleSelectConversation}
        onDeleteConversation={handleDeleteConversation}
        onNewConversation={handleNewConversation}
        isCollapsed={sidebarCollapsed}
        onToggleCollapse={() => setSidebarCollapsed(!sidebarCollapsed)}
      />

      {/* Main Chat Area */}
      <div className="flex-1 flex flex-col overflow-hidden">
        {/* Top Bar with Menu Button */}
        {sidebarCollapsed && (
          <div className="bg-white border-b border-gray-200 px-4 py-3 flex items-center gap-3">
            <button
              onClick={() => setSidebarCollapsed(false)}
              className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
              title="Show sidebar"
            >
              <Menu className="w-5 h-5 text-gray-600" />
            </button>
            <span className="text-sm font-medium text-gray-700">Analytics AI Assistant</span>
          </div>
        )}

        {/* Messages Area - Scrollable */}
        <div className="flex-1 overflow-y-auto">
          <div className="max-w-4xl mx-auto px-4 py-6">
            {/* Loading State for Conversation Switch */}
            {isLoadingConversation && (
              <div className="flex items-center justify-center py-12">
                <div className="flex items-center gap-3 text-gray-600">
                  <Loader2 className="w-5 h-5 animate-spin" />
                  <span>Loading conversation...</span>
                </div>
              </div>
            )}

            {/* Results */}
            {!isLoadingConversation && (
              <div className="space-y-6">
                {visibleBlocks.map((block, index) => (
                  <div
                    key={`${block.type}-${index}-${block.timestamp}`}
                    className="bg-white rounded-lg shadow-sm animate-in slide-in-from-bottom-4 duration-500"
                    style={{ animationDelay: `${index * 100}ms` }}
                  >
                    <BlockRenderer
                      block={block}
                      onSuggestionClick={handleSuggestionClick}
                    />
                  </div>
                ))}
              </div>
            )}

            {/* Example Questions - Show when no conversation is active */}
            {!isLoadingConversation && isNewConversation && visibleBlocks.length === 0 && !isStreaming && (
              <div className="text-center py-12 bg-white rounded-lg shadow-sm border border-gray-200">
                <div className="max-w-2xl mx-auto">
                  <MessageSquare className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                  <h3 className="text-lg font-medium text-gray-900 mb-4">Try asking questions like:</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    {exampleQuestions.map((example, index) => (
                      <button
                        key={index}
                        onClick={() => handleSuggestionClick(example)}
                        className="p-3 text-sm bg-gray-50 border border-gray-200 rounded-lg transition-all text-left text-gray-700 hover:shadow-md"
                        style={{
                          backgroundColor: '#f9fafb'
                        }}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.backgroundColor = '#f0f9ff';
                          e.currentTarget.style.borderColor = '#1C4645';
                          e.currentTarget.style.color = '#1C4645';
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.backgroundColor = '#f9fafb';
                          e.currentTarget.style.borderColor = '#e5e7eb';
                          e.currentTarget.style.color = '#374151';
                        }}
                      >
                        {example}
                      </button>
                    ))}
                  </div>
                  <p className="text-sm text-gray-500 mt-4">
                    Or type your own question in the box above
                  </p>
                </div>
              </div>
            )}

            {/* Show conversation title for active conversations */}
            {!isLoadingConversation && !isNewConversation && visibleBlocks.length === 0 && !isStreaming && (
              <div className="text-center py-12 bg-white rounded-lg shadow-sm border border-gray-200">
                <div className="max-w-2xl mx-auto">
                  <MessageSquare className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                  <h3 className="text-lg font-medium text-gray-900 mb-2">
                    {getCurrentConversation()?.title}
                  </h3>
                  <p className="text-sm text-gray-500 mb-4">
                    Conversation from {getCurrentConversation()?.timestamp.toLocaleDateString()}
                  </p>
                  <button
                    onClick={() => handleSubmitAnalysis(getCurrentConversation()?.question)}
                    className="px-4 py-2 text-white rounded-lg flex items-center gap-2 mx-auto transition-all"
                    style={{ backgroundColor: '#1C4645' }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.backgroundColor = '#ffffff';
                      e.currentTarget.style.color = '#1C4645';
                      e.currentTarget.style.border = '1px solid #1C4645';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.backgroundColor = '#1C4645';
                      e.currentTarget.style.color = '#ffffff';
                      e.currentTarget.style.border = 'none';
                    }}
                  >
                    <RefreshCw className="w-4 h-4" />
                    Re-run Analysis
                  </button>
                </div>
              </div>
            )}

            {/* Error Message */}
            {error && !visibleBlocks.some(block => block.type === 'error') && (
              <div className="bg-white rounded-lg shadow-sm">
                <ErrorBlock message="Analysis Failed" details={error} />
              </div>
            )}

            <div ref={bottomRef} />
          </div>
        </div>

        {/* Fixed Input Area at Bottom - ChatGPT Style */}
        <div className="border-t border-gray-200 bg-white ">
          <ChatInput
            question={question}
            setQuestion={setQuestion}
            onSubmit={handleSubmit}
            onCancel={cancelAnalysis}
            isStreaming={isStreaming}
            isLoadingConversation={isLoadingConversation}
            tokenLoading={tokenLoading}
            tokenError={tokenError}
            advertiserToken={advertiserToken}
          />
        </div>
      </div>
    </div>
  );
};

export default DV360Analytics;
